<template>
  <nav v-if="totalPages > 1" class="blog-pagination" aria-label="Blog pages">
    <nuxt-link
      v-if="currentPage > 1"
      :to="pagePath(currentPage - 1)"
      class="blog-pagination__step"
      rel="prev"
    >
      <span aria-hidden="true">&larr;</span> Previous
    </nuxt-link>
    <span v-else class="blog-pagination__step is-disabled" aria-hidden="true">
      <span>&larr;</span> Previous
    </span>

    <ol class="blog-pagination__pages">
      <li v-for="(item, index) in items" :key="`${item.type}-${index}`">
        <span v-if="item.type === 'gap'" class="blog-pagination__gap"
          >&hellip;</span
        >
        <!-- The current page is still a link: it keeps the control's hit
             targets uniform, and Google follows it as a self-reference. -->
        <nuxt-link
          v-else
          :to="pagePath(item.page)"
          class="blog-pagination__page"
          :class="{ 'is-current': item.page === currentPage }"
          :aria-current="item.page === currentPage ? 'page' : false"
        >
          {{ item.page }}
        </nuxt-link>
      </li>
    </ol>

    <nuxt-link
      v-if="currentPage < totalPages"
      :to="pagePath(currentPage + 1)"
      class="blog-pagination__step"
      rel="next"
    >
      Next <span aria-hidden="true">&rarr;</span>
    </nuxt-link>
    <span v-else class="blog-pagination__step is-disabled" aria-hidden="true">
      Next <span>&rarr;</span>
    </span>
  </nav>
</template>

<script>
import { blogPagePath, blogTagPath } from '@/helpers/blog-pagination'

// Below this many pages, every number is linked. That is the whole point of
// the control for SEO: with all 6 of today's pages on screen, every post sits
// two clicks from /blog. Collapsing to a window straight away would push the
// middle pages to three or four clicks and thin out how often Google crawls
// the posts on them.
const FULL_LIST_LIMIT = 12

// Once the blog outgrows that, fall back to first/last plus this many either
// side of the current page, so the control stays a usable width.
const WINDOW = 1

export default {
  name: 'BlogPagination',
  props: {
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true },
    // When set, paginate within a tag hub rather than the main listing.
    tagSlug: { type: String, default: '' },
  },
  computed: {
    // Every page number is a real link rather than prev/next only: it is what
    // keeps each post within two clicks of /blog, so crawl depth stays flat as
    // the blog grows.
    items() {
      const pages = []

      const listAll = this.totalPages <= FULL_LIST_LIMIT

      for (let page = 1; page <= this.totalPages; page++) {
        const isEdge = page === 1 || page === this.totalPages
        const isNearCurrent = Math.abs(page - this.currentPage) <= WINDOW

        if (listAll || isEdge || isNearCurrent) {
          pages.push(page)
        }
      }

      return pages.reduce((items, page, index) => {
        const previous = pages[index - 1]
        if (previous && page - previous > 1) {
          items.push({ type: 'gap' })
        }
        items.push({ type: 'page', page })
        return items
      }, [])
    },
  },
  methods: {
    pagePath(page) {
      return this.tagSlug ? blogTagPath(this.tagSlug, page) : blogPagePath(page)
    },
  },
}
</script>

<style lang="scss" scoped>
.blog-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 48px 0 8px;
}

.blog-pagination__pages {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
  }
}

.blog-pagination__page,
.blog-pagination__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  // 44px is the minimum tap target in the design system.
  min-width: var(--ps-btn-h-compact);
  height: var(--ps-btn-h-compact);
  padding: 0 12px;
  border: 1px solid var(--ps-border);
  border-radius: var(--ps-radius-md);
  background: var(--ps-white);
  color: var(--ps-body);
  font-size: var(--ps-text-small);
  font-weight: 600;
  text-decoration: none;
  transition: background-color var(--ps-transition),
    border-color var(--ps-transition), color var(--ps-transition);

  &:hover {
    border-color: var(--ps-indigo-200);
    color: var(--ps-indigo);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--ps-indigo);
    box-shadow: var(--ps-focus-ring);
  }
}

// Indigo, not green: paging is an in-app action, not a conversion CTA.
.blog-pagination__page.is-current {
  background: var(--ps-indigo);
  border-color: var(--ps-indigo);
  color: var(--ps-white);
  cursor: default;
}

.blog-pagination__step.is-disabled {
  color: var(--ps-disabled-ink);
  background: var(--ps-disabled-bg);
  border-color: var(--ps-border);
}

.blog-pagination__gap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: var(--ps-btn-h-compact);
  color: var(--ps-muted);
}

// Prev/Next labels crowd the numbers on small screens; the arrows carry the
// meaning and the numbers remain.
@media (max-width: 480px) {
  .blog-pagination__step {
    display: none;
  }
}
</style>
